// components/css-carousel/CarouselDots.tsx
'use client';

import React from 'react';
import { cn } from '@/lib/cn';

interface CarouselDotsProps {
  carouselId: string;
  className?: string;
  dotClassName?: string;
  activeDotClassName?: string;
}

interface DotProps {
  isActive: boolean;
  size?: 'regular' | 'small';
  onClick?: () => void;
}

function Dot({ isActive, size = 'regular', onClick }: DotProps) {
  const baseClasses = 'rounded-full transition-all duration-200 cursor-pointer';

  if (size === 'small') {
    // Small dots (4px) - always inactive in the design
    return (
      <button
        type="button"
        onClick={onClick}
        data-analytics-skip="true"
        className={cn(
          baseClasses,
          'w-1 h-1', // 4px
          'bg-white-40 hover:bg-white-60',
        )}
      />
    );
  }

  // Regular dots (6px)
  return (
    <button
      type="button"
      onClick={onClick}
      data-analytics-skip="true"
      className={cn(
        baseClasses,
        'w-1.5 h-1.5', // 6px
        isActive ? 'bg-white scale-100' : 'bg-white-40 hover:bg-white-60 scale-100',
      )}
    />
  );
}

export function CarouselDots({ carouselId, className }: CarouselDotsProps) {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [totalSlides, setTotalSlides] = React.useState(0);

  React.useEffect(() => {
    const updateDotStates = () => {
      const carousel = document.getElementById(carouselId) as HTMLElement;
      if (!carousel) return;

      const slides = carousel.querySelectorAll<HTMLElement>(`.${carouselId}-slide`);

      setTotalSlides(slides.length);

      // Prefer index computed by CarouselClient to avoid layout reads here.
      const raw = parseInt(carousel.dataset.currentIndex || '0', 10);
      setCurrentIndex(Number.isFinite(raw) ? raw : 0);
    };

    // Initial update
    updateDotStates();

    // Listen for carousel update events from CarouselClient
    const handleCarouselUpdate = (event: CustomEvent) => {
      if (event.detail.carouselId === carouselId) {
        setCurrentIndex(event.detail.currentIndex);
        setTotalSlides(event.detail.slidesLength);
      }
    };

    document.addEventListener('carousel-update', handleCarouselUpdate as EventListener);
    window.addEventListener('resize', updateDotStates);

    return () => {
      document.removeEventListener('carousel-update', handleCarouselUpdate as EventListener);
      window.removeEventListener('resize', updateDotStates);
    };
  }, [carouselId]);

  const goToSlide = (index: number) => {
    const event = new CustomEvent('carousel-control', {
      detail: { carouselId, action: 'goto', index },
    });
    document.dispatchEvent(event);
  };

  if (totalSlides <= 1) {
    return null; // Don't show dots if there's only one slide
  }

  const renderDots = () => {
    const dots = [];
    const maxVisibleDots = 5;

    if (totalSlides <= maxVisibleDots) {
      // Simple case: show all dots
      for (let i = 0; i < totalSlides; i++) {
        dots.push(<Dot key={i} isActive={i === currentIndex} onClick={() => goToSlide(i)} />);
      }
    } else {
      // Complex case: smart positioning with small dots at edges
      if (currentIndex === 0) {
        // Active=1st pattern
        dots.push(<Dot key={0} isActive={true} onClick={() => goToSlide(0)} />);
        dots.push(<Dot key={1} isActive={false} onClick={() => goToSlide(1)} />);
        dots.push(<Dot key={2} isActive={false} onClick={() => goToSlide(2)} />);
        dots.push(<Dot key={3} isActive={false} onClick={() => goToSlide(3)} />);
        dots.push(
          <Dot
            key="end"
            size="small"
            isActive={false}
            onClick={() => goToSlide(totalSlides - 1)}
          />,
        );
      } else if (currentIndex === 1) {
        // Active=2nd pattern
        dots.push(<Dot key={0} isActive={false} onClick={() => goToSlide(0)} />);
        dots.push(<Dot key={1} isActive={true} onClick={() => goToSlide(1)} />);
        dots.push(<Dot key={2} isActive={false} onClick={() => goToSlide(2)} />);
        dots.push(<Dot key={3} isActive={false} onClick={() => goToSlide(3)} />);
        dots.push(
          <Dot
            key="end"
            size="small"
            isActive={false}
            onClick={() => goToSlide(totalSlides - 1)}
          />,
        );
      } else if (currentIndex === totalSlides - 1) {
        // Active=Last pattern
        dots.push(<Dot key="start" size="small" isActive={false} onClick={() => goToSlide(0)} />);
        dots.push(
          <Dot key={totalSlides - 4} isActive={false} onClick={() => goToSlide(totalSlides - 4)} />,
        );
        dots.push(
          <Dot key={totalSlides - 3} isActive={false} onClick={() => goToSlide(totalSlides - 3)} />,
        );
        dots.push(
          <Dot key={totalSlides - 2} isActive={false} onClick={() => goToSlide(totalSlides - 2)} />,
        );
        dots.push(
          <Dot key={totalSlides - 1} isActive={true} onClick={() => goToSlide(totalSlides - 1)} />,
        );
      } else if (currentIndex === totalSlides - 2) {
        // Active=2nd last pattern
        dots.push(<Dot key="start" size="small" isActive={false} onClick={() => goToSlide(0)} />);
        dots.push(
          <Dot key={totalSlides - 4} isActive={false} onClick={() => goToSlide(totalSlides - 4)} />,
        );
        dots.push(
          <Dot key={totalSlides - 3} isActive={false} onClick={() => goToSlide(totalSlides - 3)} />,
        );
        dots.push(
          <Dot key={totalSlides - 2} isActive={true} onClick={() => goToSlide(totalSlides - 2)} />,
        );
        dots.push(
          <Dot key={totalSlides - 1} isActive={false} onClick={() => goToSlide(totalSlides - 1)} />,
        );
      } else {
        // Active=Middle pattern
        dots.push(<Dot key="start" size="small" isActive={false} onClick={() => goToSlide(0)} />);
        dots.push(
          <Dot
            key={currentIndex - 1}
            isActive={false}
            onClick={() => goToSlide(currentIndex - 1)}
          />,
        );
        dots.push(
          <Dot key={currentIndex} isActive={true} onClick={() => goToSlide(currentIndex)} />,
        );
        dots.push(
          <Dot
            key={currentIndex + 1}
            isActive={false}
            onClick={() => goToSlide(currentIndex + 1)}
          />,
        );
        dots.push(
          <Dot
            key="end"
            size="small"
            isActive={false}
            onClick={() => goToSlide(totalSlides - 1)}
          />,
        );
      }
    }

    return dots;
  };

  return (
    <div
      className={cn(
        // Layout
        'flex items-center justify-center w-full',
        // Figma scrim behind dots: transparent -> 20% black
        'bg-gradient-to-b from-black-0 to-black-20',
        // Size & spacing
        'h-12 p-3',
        className,
      )}
      style={
        {
          WebkitTransform: 'translateZ(0)',
          transform: 'translateZ(0)',
          willChange: 'transform',
        } as React.CSSProperties
      }
      role="tablist"
      aria-label="Carousel navigation dots"
    >
      <div className={cn('flex items-center justify-center gap-1')}>{renderDots()}</div>
    </div>
  );
}
